/**
 * PluginRenderer — Renders a plugin arche in a sandboxed iframe.
 *
 * Responsibilities:
 * - Fetches plugin config from the arche record
 * - Creates sandboxed iframe with strict CSP
 * - Establishes postMessage bridge via PluginBridgeHost
 * - Handles resize, navigation, and toast events
 * - Shows loading/error states
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import api, { API_BASE } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { toast } from '@/components/ui/use-toast';
import { addBreadcrumb } from '@/lib/telemetry';
import { useRegisterArcheAI } from '@/components/fias-ai';
import { useGlobalFiasAIOptional } from '@/components/fias-ai/hooks/GlobalFiasAIContext';
import type {
  ArcheAIAction,
  PluginDeclaredAiAction,
  PluginRuntimeAiDeclaration,
} from '@/components/fias-ai/types';
import { PluginBridgeHost } from './PluginBridge';
import { PLUGIN_IFRAME_ALLOW, PLUGIN_IFRAME_SANDBOX } from './pluginSandbox';
import { PurchaseConfirmationSheet } from './PurchaseConfirmationSheet';
import { PLUGIN_STORE_ENTITY_ID } from '@/lib/integration-entity-permissions';
import { VaultAccessGrantSheet, type VaultPickResolution } from './VaultAccessGrantSheet';
import { ArcheLoadingScreen } from './ArcheLoadingScreen';
import { FiasAIDiscoveryChip } from './FiasAIDiscoveryChip';
import { DARK_THEME, LIGHT_THEME, getThemeById, type PluginPermission } from '@fias/arche-sdk';
import { normalizeCdnBaseUrl, allVendoredLibraryAssets } from '@fias/platform-resources';

/**
 * Returns true if the bundleRef points to an S3-hosted bundle that should
 * be served through the API proxy (because the S3 bucket blocks public access).
 */
export function isS3BundleRef(bundleRef: string): boolean {
  return bundleRef.startsWith('s3://') || bundleRef.startsWith('plugin-bundles/');
}

/**
 * Resolve an `open_arche` bridge request host-side: navigate the top window to
 * another arche's page (`/a/<archeId>`). `newTab` is best-effort — the parent's
 * `window.open` can be popup-blocked because user activation does not cross the
 * iframe→host postMessage boundary, so we fall back to same-tab navigation when
 * the popup is blocked (returns null) and the click never silently no-ops.
 *
 * `targetArcheId` is already format-validated by the bridge before this runs.
 * `windowOpen` is injectable for testing. Exported for unit tests.
 */
export function openArcheFromHost(
  navigate: (path: string) => void,
  targetArcheId: string,
  newTab: boolean,
  windowOpen: (url: string, target: string, features: string) => Window | null = (url, t, f) =>
    window.open(url, t, f),
): void {
  const path = `/a/${targetArcheId}`;
  if (newTab) {
    const opened = windowOpen(path, '_blank', 'noopener,noreferrer');
    if (!opened) navigate(path);
  } else {
    navigate(path);
  }
}

interface PluginRendererProps {
  archeId: string;
  /**
   * `arches.is_trusted` — threaded from ArcheInteractionPage's metadata fetch
   * (the `getArche` response this component fetches does not carry it). Gates
   * the RUNTIME Fias AI declaration lane: non-trusted plugins are
   * manifest-only, because manifest action text is fixed at publish and passes
   * submission review, while runtime text is never reviewed. Defaults false
   * (fail closed).
   */
  isTrusted?: boolean;
}

/** Matches PluginConfig from @fias/db-types (redefined for client use) */
interface PluginConfig {
  bundleRef: string;
  manifestVersion: string;
  // Full client-safe permission union from the SDK (lockstep with db-types) —
  // see the note in PluginBridge.ts. Avoids the prior hand-listed subset that
  // omitted most scopes.
  permissions: PluginPermission[];
  sdkVersion: string;
  bundleSizeBytes: number;
  buildHash: string;
  themeId?: string;
  fontPairingId?: string;
  customTheme?: import('@fias/arche-sdk').FiasTheme;
  /** Manifest `fiasAI` block snapshotted at publish (null = explicitly none). */
  fiasAI?: { description: string; actions: PluginDeclaredAiAction[] } | null;
}

/**
 * Hard cap on the MERGED (manifest ∪ runtime) action list — the assistant
 * turn endpoint's `.strict()` schema rejects >50 actions, which would 400
 * every turn while the plugin is open. Manifest actions win the trim
 * (deterministic, and they passed publish review).
 */
const MAX_MERGED_AI_ACTIONS = 50;

/** After the user closes or minimizes the assistant panel, plugin `ai_open`
 * requests are ignored for this long — the user's dismissal outranks the
 * plugin's desire to reopen (bridge-level burst debounce is separate). */
const AI_OPEN_AFTER_DISMISS_COOLDOWN_MS = 30_000;

/** availableWhen: dumb equality against the plugin's last state snapshot. */
function aiActionAvailable(
  action: PluginDeclaredAiAction,
  state: Record<string, unknown>,
): boolean {
  if (!action.availableWhen) return true;
  return Object.entries(action.availableWhen).every(([key, value]) => state[key] === value);
}

/**
 * Strip to EXACTLY the fields the assistant turn schema accepts —
 * `availableWhen` (or any future host-only field) leaking through would 400
 * the whole turn (`archeActionSchema` is `.strict()`).
 */
function toArcheAIAction(a: PluginDeclaredAiAction): ArcheAIAction {
  return {
    id: a.id,
    description: a.description,
    parameterSchema: a.parameterSchema,
    ...(a.requiresConfirmation !== undefined
      ? { requiresConfirmation: a.requiresConfirmation }
      : {}),
    ...(a.category !== undefined ? { category: a.category } : {}),
  };
}

/**
 * Returns true if the bundleRef should be fetched through the API proxy.
 * This includes:
 * - Source tarballs (local dev auto-approve, e.g. "user/plugin-sources/.../name.tar.gz")
 * - S3 keys (new format, e.g. "plugin-bundles/arc_xxx/v1/index.html")
 * - S3 URIs (old format, e.g. "s3://bucket/plugin-bundles/...")
 *
 * The S3 bucket blocks public access, so the browser cannot load S3 URLs
 * directly. All bundles must be proxied through the API endpoint.
 */
function shouldFetchViaApi(bundleRef: string): boolean {
  // Only external HTTPS URLs (non-S3) can be loaded directly by the browser
  if (bundleRef.startsWith('https://') && !bundleRef.includes('.s3.amazonaws.com/')) {
    return false;
  }
  return true;
}

export function PluginRenderer({ archeId, isTrusted = false }: PluginRendererProps) {
  const [pluginConfig, setPluginConfig] = useState<PluginConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeHeight, setIframeHeight] = useState<number | null>(null);
  const [srcdoc, setSrcdoc] = useState<string | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  // When the plugin's SDK announces a protocol version below what the
  // host requires, we capture the version pair and render an "update
  // needed" affordance instead of leaving a misbehaving iframe mounted.
  const [incompatibleSdk, setIncompatibleSdk] = useState<{
    sdkVersion: number;
    minRequired: number;
  } | null>(null);
  // A retired op-family alias (storage_*/data_*/workspace_*/store_*) arrived
  // from an old, immutable bundle (ADR-012 Phase L Job 3 Step 2). Same "needs
  // update" treatment as an incompatible SDK, but triggered on first use of the
  // retired capability rather than at the init handshake.
  const [retiredCapability, setRetiredCapability] = useState<string | null>(null);

  // Purchase confirmation state
  const [purchaseProduct, setPurchaseProduct] = useState<{
    productIdentifier: string;
    name: string;
    description: string | null;
    priceCredits: number;
    type: string;
  } | null>(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [userBalance, setUserBalance] = useState<number | null>(null);
  const purchaseResolveRef = useRef<((value: unknown) => void) | null>(null);

  // Vault user-document picker state (vault_documents_pick host mediation)
  const [vaultPickRequest, setVaultPickRequest] = useState<{ maxDocuments: number } | null>(null);
  const [vaultPickArcheName, setVaultPickArcheName] = useState<string | null>(null);
  const vaultPickResolveRef = useRef<((value: VaultPickResolution) => void) | null>(null);

  // Fias AI plane: the plugin's runtime action declaration (trusted arches
  // only) + its last state snapshot. Both are React state — the registered
  // capabilities object is rebuilt per render and read fresh at turn time, so
  // a ref-only snapshot would hand the assistant a stale availableWhen filter.
  const [runtimeAiDecl, setRuntimeAiDecl] = useState<PluginRuntimeAiDeclaration | null>(null);
  const [aiState, setAiState] = useState<Record<string, unknown>>({});
  // Latest-ref mirror for the synchronous getStateSnapshot contract.
  const aiStateRef = useRef(aiState);
  aiStateRef.current = aiState;
  // The bridge is constructed once per iframe load; read trust via ref so the
  // callback can't capture a stale value (same idiom as themeModeRef).
  const isTrustedRef = useRef(isTrusted);
  isTrustedRef.current = isTrusted;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<PluginBridgeHost | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme: appTheme } = useTheme();
  const themeMode = appTheme === 'light' ? 'light' : 'dark';
  // The bridge's `getTheme` callback must read the CURRENT mode through this
  // ref, never the `themeMode` binding above. The bridge is constructed once,
  // at iframe load, so a plain closure captures the mode of THAT render; every
  // later `sendThemeUpdate()` then re-evaluates `getTheme` inside the stale
  // closure and pushes the ORIGINAL theme — the plugin re-renders with the
  // theme it already had, and the toggle appears dead until a hard reload
  // rebuilds the bridge. (`handleIframeLoad` lists `themeMode` in its deps,
  // but a fresh callback identity is useless: the iframe never fires `load`
  // again, so the fresh closure is never invoked.)
  //
  // Written during render (the latest-ref idiom) rather than in an effect, on
  // purpose: an effect version would tie correctness to effect ORDERING — the
  // sendThemeUpdate effect below must observe the new value in the same
  // commit — which is a subtler dependency than an idempotent render write.
  // Same idiom as the builder page's previewThemeRef and panel-kit's queueRef.
  const themeModeRef = useRef(themeMode);
  themeModeRef.current = themeMode;
  // Same latest-ref treatment for the user: `getUser` used to close over
  // `user` with the identical trap (listed in the callback deps, but the
  // iframe never re-fires `load`, so a fresh closure never installs). User
  // identity is stable within a session so it was latent rather than
  // user-visible — fixed for uniformity so the bridge options carry ONE idiom,
  // not a mix of live refs and stale closures.
  const userRef = useRef(user);
  userRef.current = user;

  // Fetch arche data to get plugin config
  useEffect(() => {
    let mounted = true;

    async function loadPlugin() {
      try {
        const result = (await api.getArche(archeId)) as Record<string, unknown>;
        const config = result.pluginConfig as PluginConfig | null;

        if (!config) {
          throw new Error('This arche does not have a valid plugin configuration');
        }

        // Fetch bundles through the API proxy (S3 bucket blocks public access).
        // credentials: 'include' is required so the fias_session cookie flows
        // to the API on production (frontend and API live on different
        // subdomains; default fetch drops cookies on cross-origin). Without
        // it, signed-in users hit 401 on non-public plugins because
        // optionalAuth sees no token. Same pattern as api.ts:181.
        if (shouldFetchViaApi(config.bundleRef)) {
          // Cache-bust on the build hash (defence in depth over the server's
          // ETag): the bundle URL is otherwise version-stable, so a stale
          // HTTP-cache entry from a prior publish could be reused even if an
          // intermediary ignores the response's Cache-Control. The hash is
          // fetched in this same effect run, so it always matches the config
          // we're about to render. NOTE: this only affects the NEXT mount /
          // navigation / reload — the effect is keyed on [archeId] and never
          // re-runs for an already-mounted plugin when a new version publishes.
          const bundleUrl = config.buildHash
            ? `${API_BASE}/arches/${archeId}/plugin-bundle?h=${encodeURIComponent(config.buildHash)}`
            : `${API_BASE}/arches/${archeId}/plugin-bundle`;
          const resp = await fetch(bundleUrl, {
            credentials: 'include',
          });
          if (!resp.ok) {
            const body = await resp.json().catch(() => null);
            const msg =
              (body as Record<string, Record<string, string>> | null)?.error?.message ||
              `Failed to load plugin bundle (${resp.status})`;
            throw new Error(msg);
          }
          const html = await resp.text();
          // The bundle was built with `__FIAS_API_BASE__` placeholders in
          // `<base href>` and the CSP `img-src` / `media-src` / `font-src`
          // allowlists. The placeholder is mis-named historically — it's
          // only used for plugin-asset URLs (the bridge call goes via
          // postMessage; the iframe CSP is `connect-src 'none'`).
          //
          // Prefer the dedicated plugin-assets CDN when configured —
          // CloudFront caches the immutable `/v1/arches/<id>/plugin-
          // assets/<version>/<file>` paths so repeated viewers don't hit
          // the API tier for each asset. Falls back to the API origin
          // when the env var is empty (local dev + envs before the CDN
          // stack deploys). Either origin serves the same path
          // (CloudFront forwards uncached requests to the same route),
          // so changing the substitution origin is a strict caching
          // optimization with no semantic difference.
          const pluginAssetsCdnBase = import.meta.env.VITE_PLUGIN_ASSETS_CDN_BASE as
            | string
            | undefined;
          const pluginAssetsOrigin =
            pluginAssetsCdnBase && pluginAssetsCdnBase.length > 0
              ? pluginAssetsCdnBase.replace(/\/$/, '')
              : new URL(API_BASE, window.location.origin).origin;
          const substituted = html.split('__FIAS_API_BASE__').join(pluginAssetsOrigin);
          if (mounted) {
            setSrcdoc(substituted);
            setPluginConfig(config);
            setLoading(false);
          }
          return;
        }

        if (mounted) {
          setPluginConfig(config);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load plugin');
          setLoading(false);
        }
      }
    }

    loadPlugin();
    return () => {
      mounted = false;
    };
  }, [archeId]);

  // Set up bridge when iframe loads
  const handleIframeLoad = useCallback(() => {
    if (!iframeRef.current || !pluginConfig) return;

    setIframeReady(true);

    // Clean up previous bridge
    bridgeRef.current?.destroy();

    bridgeRef.current = new PluginBridgeHost({
      archId: archeId,
      permissions: pluginConfig.permissions,
      iframe: iframeRef.current,
      onResize: (height) => setIframeHeight(height),
      onToast: (message, variant) => {
        toast({
          description: message,
          variant: variant === 'error' ? 'destructive' : undefined,
        });
      },
      onNavigate: (path) => {
        navigate(path);
      },
      onOpenArche: (targetArcheId, newTab) => openArcheFromHost(navigate, targetArcheId, newTab),
      // Runtime Fias AI declaration — TRUSTED arches only (manifest text is
      // publish-reviewed; runtime text is not). The bridge has already
      // enforced permission + bounds; trust is the renderer's to decide
      // because it holds the metadata.
      onAiActionsDeclare: (declaration) => {
        if (!isTrustedRef.current) {
          addBreadcrumb('plugin-ai', 'runtime ai_actions_declare ignored (arche not trusted)', {
            archeId,
          });
          return;
        }
        setRuntimeAiDecl(declaration);
      },
      onAiState: (state) => setAiState(state),
      onAiOpenRequest: () => {
        const ctx = fiasAIGlobalRef.current;
        if (!ctx) return;
        if (ctx.isPanelOpen && !ctx.isMinimized) return; // already visible
        // A MINIMIZED panel is a standing dismissal, not a gap to jump
        // through: the user parked it (possibly before this renderer even
        // mounted, where the falling-edge tracker below can't have seen the
        // transition). Expanding it is the plugin overriding the user.
        if (ctx.isMinimized) {
          addBreadcrumb('plugin-ai', 'ai_open ignored (assistant is minimized by the user)', {
            archeId,
          });
          return;
        }
        if (Date.now() - lastPanelDismissAtRef.current < AI_OPEN_AFTER_DISMISS_COOLDOWN_MS) {
          addBreadcrumb('plugin-ai', 'ai_open ignored (user recently dismissed the panel)', {
            archeId,
          });
          return;
        }
        ctx.openPanel();
      },
      getTheme: () => {
        if (pluginConfig.customTheme) return pluginConfig.customTheme;
        if (pluginConfig.themeId) {
          const entry = getThemeById(pluginConfig.themeId);
          if (entry) return entry.theme;
        }
        // Via the ref, NOT the closure — see themeModeRef above.
        return themeModeRef.current === 'dark' ? DARK_THEME : LIGHT_THEME;
      },
      // Self-hosted vendored-library asset URLs (only consulted for
      // `sandbox:vendored-libraries` plugins). Resolved from the client's
      // platform-resources CDN env; empty when unconfigured (local dev / pre-CDN
      // staging) → plugin degrades. The full registry is injected; the CSP
      // (widened per declared dep at build time) is what gates actual loading.
      getVendoredAssets: () =>
        allVendoredLibraryAssets(
          normalizeCdnBaseUrl(
            import.meta.env.VITE_PLATFORM_RESOURCES_CDN_DOMAIN as string | undefined,
          ),
        ),
      getUser: async () => ({
        // Via the ref, NOT the closure — see userRef above.
        userId: userRef.current?.userId ?? '',
        displayName: userRef.current?.username ?? 'User',
        avatar: null,
      }),
      bridgeApiUrl: API_BASE,
      // Plugin iframes have an opaque origin and cannot send the
      // parent's session cookie. They authenticate to the bridge with
      // a per-arche capability JWT (audience=plugin-bridge, 5min TTL,
      // archeId-scoped). The token has NO authority outside the
      // /v1/plugins/bridge/:archId route — a leak cannot drain credits
      // or read vault data via any other endpoint.
      getAuthToken: async () => (await authApi.getPluginBridgeToken(archeId)) ?? '',
      // One-shot expired-token recovery (see callBackendOp): bust the cached
      // token so the retry mints fresh instead of re-sending the stale one.
      onAuthTokenExpired: () => authApi.invalidatePluginBridgeToken(archeId),
      onIncompatibleSdk: (sdkVersion, minRequired) => {
        setIncompatibleSdk({ sdkVersion, minRequired });
      },
      onRetiredCapability: (op) => {
        setRetiredCapability(op);
      },
      onPurchaseRequest: (product) => {
        return new Promise((resolve) => {
          // Fetch user balance for the confirmation sheet
          api
            .get<{ balance: number }>('/credits/balance')
            .then((res) => setUserBalance(res.data?.balance ?? null))
            .catch(() => setUserBalance(null));
          setPurchaseProduct(product);
          purchaseResolveRef.current = resolve;
        });
      },
      onVaultPickRequest: (request) => {
        return new Promise<VaultPickResolution>((resolve) => {
          // Arche display name for the consent copy — best-effort.
          api
            .get<{ name?: string }>(`/arches/${archeId}`)
            .then((res) => setVaultPickArcheName(res.data?.name ?? null))
            .catch(() => setVaultPickArcheName(null));
          setVaultPickRequest(request);
          vaultPickResolveRef.current = resolve;
        });
      },
    });

    bridgeRef.current.init();
    // NOTE: `themeMode` and `user` are deliberately NOT deps — `getTheme` and
    // `getUser` read the live values through refs. Listing them only churns
    // the callback identity, which cannot help (the iframe's `load` event has
    // already fired) and previously disguised the stale-closure bug as fixed.
  }, [archeId, pluginConfig, navigate]);

  // Send theme update when mode changes
  useEffect(() => {
    bridgeRef.current?.sendThemeUpdate();
  }, [themeMode]);

  // Fias AI plane — declarations are per-plugin; drop them when the arche
  // swaps (the bridge is torn down with the old iframe alongside).
  useEffect(() => {
    setRuntimeAiDecl(null);
    setAiState({});
  }, [archeId]);

  const manifestAi = pluginConfig?.fiasAI ?? null;

  // `ai_open` (discoverability Part 2): the bridge debounces bursts; the
  // renderer owns panel-state policy because only IT can see the panel. Two
  // rules on top of the bridge's: no-op while the panel is visible, and a
  // cooldown after the user closes/minimizes it — "get out of my way" must
  // stick against a plugin that immediately re-requests.
  const fiasAIGlobal = useGlobalFiasAIOptional();
  const fiasAIGlobalRef = useRef(fiasAIGlobal);
  fiasAIGlobalRef.current = fiasAIGlobal;
  const lastPanelDismissAtRef = useRef(0);
  const panelWasVisibleRef = useRef(false);
  useEffect(() => {
    const visible = !!fiasAIGlobal && fiasAIGlobal.isPanelOpen && !fiasAIGlobal.isMinimized;
    if (panelWasVisibleRef.current && !visible) lastPanelDismissAtRef.current = Date.now();
    panelWasVisibleRef.current = visible;
  }, [fiasAIGlobal]);

  // Manifest ∪ runtime, manifest winning id collisions (it passed publish
  // review; a runtime redefinition of a reviewed action must not stick).
  const mergedAiActions = useMemo(() => {
    const runtime = isTrusted ? runtimeAiDecl : null;
    const merged: PluginDeclaredAiAction[] = [...(manifestAi?.actions ?? [])];
    const ids = new Set(merged.map((a) => a.id));
    for (const action of runtime?.actions ?? []) {
      if (ids.has(action.id)) continue;
      ids.add(action.id);
      merged.push(action);
    }
    if (merged.length > MAX_MERGED_AI_ACTIONS) {
      // Never silent: the trim is visible in telemetry, and deterministic
      // (manifest first) so which verbs survive doesn't depend on timing.
      addBreadcrumb('plugin-ai', 'merged AI action list trimmed to the turn-schema cap', {
        archeId,
        declared: merged.length,
        cap: MAX_MERGED_AI_ACTIONS,
      });
      return merged.slice(0, MAX_MERGED_AI_ACTIONS);
    }
    return merged;
  }, [manifestAi, runtimeAiDecl, isTrusted, archeId]);

  const visibleAiActions = useMemo(
    () => mergedAiActions.filter((a) => aiActionAvailable(a, aiState)).map(toArcheAIAction),
    [mergedAiActions, aiState],
  );

  // Register with the global Fias AI window — the same UI plane every
  // internal arche page uses; the assistant sees a plugin arche exactly the
  // way it sees email or image-editor. The description ALWAYS carries the
  // full capability summary (including availableWhen-hidden verbs) so a
  // filtered-out action reads as "needs a document open first", never
  // "can't do that".
  useRegisterArcheAI(
    mergedAiActions.length > 0
      ? {
          archeId,
          enabled: true,
          archeDescription: [manifestAi?.description, isTrusted ? runtimeAiDecl?.description : null]
            .filter(Boolean)
            .join('\n'),
          actions: visibleAiActions,
          // TRUST BOUNDARY (ADR-017): the snapshot returned here is sent as
          // archeContext.state and interpolated VERBATIM into the assistant's
          // system prompt server-side. For a non-trusted arche that would be
          // an unreviewed free-text channel into the prompt — the exact thing
          // the manifest-only policy exists to prevent — so untrusted state
          // stays HOST-SIDE ONLY: it still drives availableWhen filtering
          // above, but the assistant sees an empty snapshot.
          getStateSnapshot: () => (isTrusted ? aiStateRef.current : {}),
          applyAction: (actionId, params) => {
            // Only declared ids are dispatchable — the model cannot probe the
            // iframe with arbitrary action names.
            if (!mergedAiActions.some((a) => a.id === actionId)) {
              throw new Error(`Unknown action "${actionId}" — the plugin does not declare it.`);
            }
            const bridge = bridgeRef.current;
            if (!bridge) throw new Error('The plugin is not running.');
            // Async relay: resolves on the plugin's ai_action_result; the
            // assistant loop awaits this before telling the model it applied.
            return bridge.dispatchAiAction(actionId, params);
          },
          sessionKey: `plugin:${archeId}`,
        }
      : null,
  );

  // Clean up bridge and pending purchase on unmount
  useEffect(() => {
    return () => {
      if (purchaseResolveRef.current) {
        purchaseResolveRef.current({ success: false, error: 'USER_CANCELED' });
        purchaseResolveRef.current = null;
      }
      if (vaultPickResolveRef.current) {
        vaultPickResolveRef.current({ canceled: true });
        vaultPickResolveRef.current = null;
      }
      bridgeRef.current?.destroy();
    };
  }, []);

  const handleVaultPickResolve = useCallback((result: VaultPickResolution) => {
    vaultPickResolveRef.current?.(result);
    vaultPickResolveRef.current = null;
    setVaultPickRequest(null);
  }, []);

  const handlePurchaseConfirm = useCallback(async () => {
    if (!purchaseProduct || !bridgeRef.current) return;
    setPurchaseLoading(true);
    try {
      // Same-origin call — use cookie auth (the api wrapper handles
      // credentials:'include' + CSRF). No bearer token round-trip
      // needed; this isn't running inside the sandboxed iframe.
      //
      // The store_purchase alias was retired (ADR-012 Phase L Job 3 Step 2); the
      // host executes the confirmed charge through the generic entity_invoke op
      // against the store entity. The server wraps the store result in `{ output }`
      // (mirrors handleStoreOp's entity_invoke store branch in PluginBridge.ts),
      // so unwrap it back to the legacy bare shape the sheet's resolver expects.
      const { data } = await api.post<{ output?: unknown }>(`/plugins/bridge/${archeId}`, {
        type: 'entity_invoke',
        payload: {
          entityId: PLUGIN_STORE_ENTITY_ID,
          payload: { op: 'purchase', productIdentifier: purchaseProduct.productIdentifier },
        },
      });
      const result = ((data as { output?: unknown })?.output ?? data) as {
        success: boolean;
        error?: string;
      };
      purchaseResolveRef.current?.(result);
    } catch {
      purchaseResolveRef.current?.({ success: false, error: 'Purchase failed' });
    } finally {
      setPurchaseLoading(false);
      setPurchaseProduct(null);
      purchaseResolveRef.current = null;
    }
  }, [purchaseProduct, archeId]);

  const handlePurchaseCancel = useCallback(() => {
    purchaseResolveRef.current?.({ success: false, error: 'USER_CANCELED' });
    setPurchaseProduct(null);
    purchaseResolveRef.current = null;
  }, []);

  if (loading) {
    return <ArcheLoadingScreen />;
  }

  if (error) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Plugin Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!pluginConfig) return null;

  if (incompatibleSdk) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Plugin needs update
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-muted-foreground">
              This plugin was built against an older version of the Fias SDK (v
              {incompatibleSdk.sdkVersion}) than the platform currently supports (v
              {incompatibleSdk.minRequired}+). The plugin author needs to rebuild with an updated
              SDK.
            </p>
            <p className="text-sm text-muted-foreground">
              If you're the contributor, run{' '}
              <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                pnpm up @fias/arche-sdk
              </code>{' '}
              in your plugin project and publish a new version.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (retiredCapability) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Plugin needs update
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-muted-foreground">
              This plugin uses a capability that has been retired from the Fias platform. The plugin
              author needs to rebuild it against a current version of the Fias SDK.
            </p>
            <p className="text-sm text-muted-foreground">
              If you're the contributor, run{' '}
              <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                pnpm up @fias/arche-sdk
              </code>{' '}
              in your plugin project and publish a new version.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const iframeProps = srcdoc ? { srcDoc: srcdoc } : { src: pluginConfig.bundleRef };

  return (
    <div className="relative w-full h-full min-h-0">
      <iframe
        ref={iframeRef}
        {...iframeProps}
        onLoad={handleIframeLoad}
        sandbox={PLUGIN_IFRAME_SANDBOX}
        allow={PLUGIN_IFRAME_ALLOW}
        referrerPolicy="no-referrer"
        className="w-full border-0 block"
        style={{
          height: iframeHeight != null ? `${iframeHeight}px` : '100%',
          background: 'hsl(var(--background))',
          visibility: iframeReady ? 'visible' : 'hidden',
        }}
        title={`Plugin: ${archeId}`}
      />
      {!iframeReady && (
        <div className="absolute inset-0">
          <ArcheLoadingScreen />
        </div>
      )}
      <PurchaseConfirmationSheet
        open={purchaseProduct !== null}
        product={purchaseProduct}
        userBalance={userBalance}
        loading={purchaseLoading}
        onConfirm={handlePurchaseConfirm}
        onCancel={handlePurchaseCancel}
      />
      <VaultAccessGrantSheet
        open={vaultPickRequest !== null}
        archeId={archeId}
        archeName={vaultPickArcheName}
        maxDocuments={vaultPickRequest?.maxDocuments ?? 25}
        onResolve={handleVaultPickResolve}
      />
      {/* Host-chrome discoverability for the plugin's Fias AI surface —
          rendered only once the iframe is up (a chip over the loading
          screen reads as UI noise) and only when the plugin actually
          declares actions. See FiasAIDiscoveryChip for the rules. */}
      {iframeReady && mergedAiActions.length > 0 && <FiasAIDiscoveryChip archeId={archeId} />}
    </div>
  );
}
